import { Paintbrush, Eraser, Undo2, Redo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEditorStore, BRUSH_SIZE_MIN, BRUSH_SIZE_MAX } from '../../../stores/editor.js';

interface ToolbarProps {
  disabled?: boolean;
}

interface ToolButtonProps {
  active?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  title: string;
  kbd?: string;
  onClick: () => void;
  children: React.ReactNode;
  ariaPressed?: boolean;
}

function ToolButton({
  active = false,
  disabled = false,
  ariaLabel,
  title,
  kbd,
  onClick,
  ariaPressed,
  children,
}: ToolButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={title}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      {...(ariaPressed !== undefined ? { 'aria-pressed': ariaPressed } : {})}
      style={{
        appearance: 'none',
        background: active ? 'rgba(255,255,255,0.04)' : 'transparent',
        border: 0,
        width: '36px',
        height: '36px',
        color: disabled
          ? 'var(--color-text-muted)'
          : active
            ? 'var(--color-accent-lime)'
            : 'var(--color-text-muted)',
        borderRadius: '5px',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'color 120ms, background-color 120ms',
      }}
    >
      {children}
      {kbd && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '3px',
            fontFamily: 'var(--font-mono)',
            fontSize: '8px',
            fontWeight: 500,
            color: active ? 'var(--color-text-secondary)' : 'var(--color-text-muted)',
            opacity: 0.7,
            lineHeight: 1,
          }}
        >
          {kbd}
        </span>
      )}
    </button>
  );
}

export function Toolbar({ disabled = false }: ToolbarProps) {
  const { t } = useTranslation('editor');
  const tool = useEditorStore((s) => s.tool);
  const brushSize = useEditorStore((s) => s.brushSize);
  const setTool = useEditorStore((s) => s.setTool);
  const setBrushSize = useEditorStore((s) => s.setBrushSize);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const undoStackLength = useEditorStore((s) => s.undoStack.length);
  const redoStackLength = useEditorStore((s) => s.redoStack.length);

  const undoDisabled = disabled || undoStackLength === 0;
  const redoDisabled = disabled || redoStackLength === 0;

  return (
    <div
      role="toolbar"
      aria-label={t('toolbar.drawingTools')}
      aria-disabled={disabled}
      data-testid="editor-toolbar"
      style={{
        position: 'absolute',
        top: '50%',
        left: '20px',
        transform: 'translateY(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '8px',
        backgroundColor: 'rgba(18, 18, 22, 0.45)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '12px',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 10px 30px rgba(0, 0, 0, 0.35)',
        zIndex: 20,
        pointerEvents: disabled ? 'none' : 'auto',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <ToolButton
        ariaPressed={tool === 'brush'}
        active={tool === 'brush'}
        disabled={disabled}
        ariaLabel={t('toolbar.brushTool')}
        title={t('toolbar.brush')}
        kbd="B"
        onClick={() => setTool('brush')}
      >
        <Paintbrush size={16} aria-hidden="true" />
      </ToolButton>

      <ToolButton
        ariaPressed={tool === 'eraser'}
        active={tool === 'eraser'}
        disabled={disabled}
        ariaLabel={t('toolbar.eraserTool')}
        title={t('toolbar.eraser')}
        kbd="E"
        onClick={() => setTool('eraser')}
      >
        <Eraser size={16} aria-hidden="true" />
      </ToolButton>

      <div
        aria-hidden="true"
        style={{
          height: '1px',
          backgroundColor: 'var(--color-border)',
          margin: '4px 6px',
        }}
      />

      <ToolButton
        disabled={undoDisabled}
        ariaLabel={t('toolbar.undo')}
        title={t('toolbar.undo')}
        kbd="Z"
        onClick={() => undo()}
      >
        <Undo2 size={16} aria-hidden="true" />
      </ToolButton>

      <ToolButton
        disabled={redoDisabled}
        ariaLabel={t('toolbar.redo')}
        title={t('toolbar.redo')}
        kbd="⇧Z"
        onClick={() => redo()}
      >
        <Redo2 size={16} aria-hidden="true" />
      </ToolButton>

      <div
        aria-hidden="true"
        style={{
          height: '1px',
          backgroundColor: 'var(--color-border)',
          margin: '4px 6px',
        }}
      />

      {/* Inline vertical brush-size control. Slim — sits inside the rail
          and stays out of the way until the user reaches for it. The
          numeric value uses tabular-nums so it doesn't jitter as it ticks. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 0 4px',
        }}
      >
        <input
          type="range"
          aria-label={t('toolbar.brushSize')}
          aria-valuemin={BRUSH_SIZE_MIN}
          aria-valuemax={BRUSH_SIZE_MAX}
          aria-valuenow={brushSize}
          min={BRUSH_SIZE_MIN}
          max={BRUSH_SIZE_MAX}
          value={brushSize}
          disabled={disabled}
          onChange={(e) => setBrushSize(Number(e.target.value))}
          style={{
            writingMode: 'vertical-lr',
            direction: 'rtl',
            width: '20px',
            height: '90px',
            accentColor: 'var(--color-accent-lime)',
            cursor: disabled ? 'default' : 'pointer',
          }}
        />
        <span
          aria-hidden="true"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '10px',
            color: 'var(--color-text-muted)',
            fontVariantNumeric: 'tabular-nums',
            userSelect: 'none',
          }}
        >
          {brushSize}
        </span>
      </div>
    </div>
  );
}
